import React, { useState } from 'react';
import PrototypeShell from './PrototypeShell';
import {
  ClipboardText,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Info,
  ShieldCheck,
} from '@phosphor-icons/react';

const sections = [
  {
    id: 'income',
    label: 'Income & Employment',
    questions: [
      {
        id: 'employment_status',
        prompt: 'Which best describes your current employment?',
        type: 'single',
        options: [
          'Working full-time',
          'Working part-time',
          'Recently started a new job',
          'In training/school',
          'Not currently working',
          'Prefer not to say',
        ],
      },
      {
        id: 'income_stability',
        prompt: 'How steady is your income month to month?',
        type: 'scale',
        scale: [
          { v: 1, l: 'Very unpredictable' },
          { v: 2, l: 'Somewhat unpredictable' },
          { v: 3, l: 'Somewhat steady' },
          { v: 4, l: 'Very steady' },
        ],
      },
    ],
  },
  {
    id: 'expenses',
    label: 'Expenses & Bills',
    questions: [
      {
        id: 'bills_on_time',
        prompt: 'In the last 3 months, how often were you able to pay your bills on time?',
        type: 'scale',
        scale: [
          { v: 1, l: 'Rarely' },
          { v: 2, l: 'Sometimes' },
          { v: 3, l: 'Most of the time' },
          { v: 4, l: 'Always' },
        ],
      },
      {
        id: 'expense_tracking',
        prompt: 'Do you track your expenses?',
        type: 'single',
        options: ['Yes, in an app', 'Yes, on paper or in a spreadsheet', 'Sometimes', 'No'],
      },
    ],
  },
  {
    id: 'savings',
    label: 'Savings & Emergency Fund',
    questions: [
      {
        id: 'emergency_fund',
        prompt: 'If an unexpected $400 expense came up, could you cover it?',
        type: 'single',
        options: [
          'Yes, from savings',
          'Yes, but I\'d need to use a credit card',
          'Maybe — I\'d need to borrow or delay',
          'No',
          'Prefer not to say',
        ],
      },
    ],
  },
  {
    id: 'debt',
    label: 'Debt & Credit',
    questions: [
      {
        id: 'debt_stress',
        prompt: 'How much stress does your current debt cause you?',
        type: 'scale',
        scale: [
          { v: 1, l: 'None' },
          { v: 2, l: 'A little' },
          { v: 3, l: 'A lot' },
          { v: 4, l: 'Overwhelming' },
        ],
      },
    ],
  },
  {
    id: 'goals',
    label: 'Goals & Confidence',
    questions: [
      {
        id: 'confidence',
        prompt: 'How confident do you feel managing money right now?',
        type: 'scale',
        scale: [
          { v: 1, l: 'Not confident' },
          { v: 2, l: 'A little confident' },
          { v: 3, l: 'Fairly confident' },
          { v: 4, l: 'Very confident' },
        ],
      },
      {
        id: 'top_goal',
        prompt: 'What\'s the most important financial goal for you right now?',
        type: 'single',
        options: [
          'Build savings for emergencies',
          'Pay down debt',
          'Understand my paycheck',
          'Improve credit',
          'Save for something specific',
          'Other',
        ],
      },
    ],
  },
];

const totalQuestions = sections.reduce((n, s) => n + s.questions.length, 0);
const AVG_SECONDS_PER_QUESTION = 50; // ~50 seconds per question for a thoughtful answer

const WorkforceAssessment = () => {
  const [sectionIndex, setSectionIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const section = sections[sectionIndex];
  const answered = Object.keys(answers).length;
  const overallPct = Math.round((answered / totalQuestions) * 100);
  const isLast = sectionIndex === sections.length - 1;
  const isFirst = sectionIndex === 0;
  const remainingQuestions = Math.max(totalQuestions - answered, 0);
  const remainingMinutes = Math.max(Math.ceil((remainingQuestions * AVG_SECONDS_PER_QUESTION) / 60), 1);

  const setAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  return (
    <PrototypeShell
      title="Financial Readiness Assessment"
      subtitle="Baseline assessment · 5 short sections · about 7 minutes total"
    >
      {/* Purpose intro */}
      <div className="mb-6 rounded-2xl bg-white border border-surface-border shadow-fluent-sm p-6" data-testid="assessment-intro">
        <h2 className="font-heading text-xl text-ink mb-2">Why we ask these questions</h2>
        <p className="text-sm text-ink-muted leading-relaxed max-w-3xl">
          This short assessment helps us understand your starting point so we can build an action plan
          that fits your life — not a generic checklist. Your answers stay private; your coach only sees
          aggregate readiness unless you decide to share more. You&apos;ll retake the assessment later so
          you can see your own progress over time.
        </p>
      </div>

      {/* Privacy strip */}
      <div className="mb-6 flex items-center gap-3 bg-brand-navy/5 border border-brand-navy/10 rounded-xl px-4 py-3 text-sm">
        <ShieldCheck size={18} weight="duotone" className="text-brand-navy flex-shrink-0" />
        <p className="text-ink-muted">
          Your answers are private. Your coach sees only aggregate readiness — never individual responses —
          unless you choose to share.{' '}
          <a href="#" className="underline text-brand-navy hover:text-brand-gold">
            How your data is used
          </a>
        </p>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2 text-xs uppercase tracking-widest font-semibold flex-wrap gap-2">
          <span className="text-ink-soft">
            Section {sectionIndex + 1} of {sections.length} · {section.label}
          </span>
          <span className="text-ink-soft flex items-center gap-3">
            <span data-testid="assessment-time-remaining">
              ⏱ About {remainingMinutes} min remaining
            </span>
            <span data-testid="assessment-progress-label">
              {answered} of {totalQuestions} answered · {overallPct}%
            </span>
          </span>
        </div>
        <div className="h-2 bg-surface-muted rounded-full overflow-hidden" data-testid="assessment-progress-bar">
          <div
            className="h-full bg-brand-gold transition-all duration-300"
            style={{ width: `${overallPct}%` }}
          />
        </div>
        <div className="mt-3 flex gap-1.5">
          {sections.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setSectionIndex(i)}
              className={`flex-1 text-[10px] uppercase tracking-widest font-semibold py-1.5 rounded-md transition ${
                i === sectionIndex
                  ? 'bg-brand-navy text-white'
                  : i < sectionIndex
                  ? 'bg-signal-success-soft text-signal-success'
                  : 'bg-surface-muted text-ink-soft hover:bg-surface-border'
              }`}
              data-testid={`section-tab-${s.id}`}
            >
              {i < sectionIndex ? '✓ ' : ''}
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Section body */}
      <section className="bg-white rounded-2xl border border-surface-border shadow-fluent-sm p-6 md:p-8">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardText size={18} weight="duotone" className="text-brand-navy" />
          <h2 className="font-heading text-2xl text-ink">{section.label}</h2>
        </div>
        <p className="text-sm text-ink-muted mb-8">
          There are no right or wrong answers. Choose what best reflects your situation today.
        </p>

        <div className="space-y-8">
          {section.questions.map((q, qi) => (
            <div key={q.id} data-testid={`question-${q.id}`}>
              <div className="flex items-start gap-2 mb-3">
                <span className="text-[11px] font-heading text-brand-gold mt-1">Q{qi + 1}</span>
                <label className="font-semibold text-ink leading-snug">{q.prompt}</label>
              </div>

              {q.type === 'single' && (
                <div className="grid sm:grid-cols-2 gap-2 ml-6">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setAnswer(q.id, opt)}
                      className={`text-left text-sm rounded-lg border px-4 py-3 transition ${
                        answers[q.id] === opt
                          ? 'border-brand-navy bg-brand-navy/5 text-brand-navy font-semibold'
                          : 'border-surface-border bg-white text-ink-muted hover:border-brand-gold hover:bg-brand-gold/5'
                      }`}
                      data-testid={`option-${q.id}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {q.type === 'scale' && (
                <div className="ml-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {q.scale.map((s) => (
                    <button
                      key={s.v}
                      onClick={() => setAnswer(q.id, s.v)}
                      className={`rounded-xl border-2 px-4 py-4 text-center transition ${
                        answers[q.id] === s.v
                          ? 'border-brand-navy bg-brand-navy text-white shadow-fluent-md'
                          : 'border-surface-border bg-white text-ink hover:border-brand-gold hover:bg-brand-gold/5'
                      }`}
                      data-testid={`scale-${q.id}-${s.v}`}
                    >
                      <div className="font-heading text-2xl leading-none mb-2">{s.v}</div>
                      <div
                        className={`text-sm font-semibold leading-tight ${
                          answers[q.id] === s.v ? 'text-white' : 'text-ink'
                        }`}
                      >
                        {s.l}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Nav buttons */}
        <div className="mt-10 flex items-center justify-between border-t border-surface-border pt-6">
          <button
            onClick={() => !isFirst && setSectionIndex(sectionIndex - 1)}
            disabled={isFirst}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border border-surface-border text-ink-muted hover:text-brand-navy hover:border-brand-navy transition disabled:opacity-40 disabled:pointer-events-none"
            data-testid="prev-section-button"
          >
            <ArrowLeft size={14} weight="bold" /> Back
          </button>
          <div className="text-xs text-ink-soft flex items-center gap-1">
            <Info size={12} /> Your progress saves automatically
          </div>
          {!isLast ? (
            <button
              onClick={() => setSectionIndex(sectionIndex + 1)}
              className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-lg bg-brand-navy text-white hover:bg-brand-navy-elevated transition"
              data-testid="next-section-button"
            >
              Next section <ArrowRight size={14} weight="bold" />
            </button>
          ) : (
            <button
              className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-lg bg-brand-gold text-brand-navy hover:bg-brand-gold-hover transition"
              data-testid="submit-assessment-button"
            >
              <CheckCircle size={16} weight="fill" /> Complete assessment
            </button>
          )}
        </div>
      </section>

      {/* Post-submit preview */}
      <div className="mt-8 rounded-2xl border border-dashed border-brand-gold/40 bg-brand-gold/5 p-5 text-sm text-ink-muted">
        <span className="font-semibold text-brand-navy">After submission:</span> we generate your personalized
        Action Plan and share aggregate readiness (never individual answers) with your program administrator
        for outcomes reporting. You can review and edit what&apos;s shared any time from Profile → My Shared Information.
      </div>
    </PrototypeShell>
  );
};

export default WorkforceAssessment;
